/**
 * The single-animal detail page (`/animal/:id`, `src/pages/AnimalView`).
 *
 * Mirrors `GET /animals/{id}` (swagger: `HealthSystemDomainAnimalsResultAnimalViewModel`, returned by
 * `animalsGetAnimalById`) plus the per-animal observations list from `GET /animals/{id}/observations`
 * (`HealthSystemDomainAnimalsModelsAnimalObservationViewModel`, via `animalsGetObservations`). Only the
 * fields the page and its render-only children (`AnimalViewFosterMotherHistory`,
 * `AnimalViewSlaughterhouse`, `AnimalViewWeightsAnalyticsGraph`, `AnimalViewNotes`) actually read are
 * carried over. Field names match the API exactly so the two stay easy to diff.
 *
 * A handful of DTO fields the generated client marks as always-present (`disease`, `mortalityReason`,
 * `location`, `nipples`, `weight`, `slaughterhouseData`) are treated as possibly absent here anyway,
 * because the page already guards every one of them with a truthiness or optional-chaining check before
 * reading further - that guard is the real contract, not the generated type.
 */
import {
  HealthSystemDALModelAnimalsDataAnimalSlaughterhouseData,
  HealthSystemDALModelAnimalsDataAnimalWeight,
  HealthSystemDomainAnimalsModelsAnimalObservationViewModel,
  HealthSystemDomainAnimalsResultAnimalViewModel
} from '../api/HealthSystemApi';
import { AnimalGender, AnimalType, AnimalWeightCategory, WeightType } from './animal';

/** A single weight reading: birth, weaning, ten-weeks, slaughter, or a foster transfer. */
export interface AnimalWeightMeasurement {
  /** ISO date-time the reading was taken. */
  date: string;
  unit: WeightType;
  /** Value in `unit`. `renderWeightCellDoubleWeightWithDate` treats a value of 0 or less as "no reading". */
  value: number;
}

export interface AnimalWeight {
  birthed: AnimalWeightMeasurement;
  weaned: AnimalWeightMeasurement;
  tenWeeks: AnimalWeightMeasurement;
  slaughtered: AnimalWeightMeasurement;
  /** Grams/day average growth rate. Null when the API has not computed one yet. */
  weightPerDay: number | null;
  /**
   * Every individual weight reading behind `birthed`/`weaned`/`tenWeeks`/`slaughtered`, used by
   * `AnimalViewWeightsGraph`'s "weight over time" line chart. Empty when there is nothing to plot.
   */
  weights: AnimalWeightMeasurement[];
}

export interface AnimalWeightAnalyticChartPoint {
  /** Age in days at the reading; the graph treats day 0 as day 1. */
  daysAge: number;
  /** How many individual readings this point aggregates. */
  totalCount: number;
  /** Weight in grams. */
  value: number;
}

/** One trend line in the "weight over time" chart - one per weight milestone (birth, weaned, ...). */
export interface AnimalWeightAnalytic {
  name: string;
  chartData: AnimalWeightAnalyticChartPoint[];
}

/** Slaughterhouse feedback for this animal. Null until the slaughterhouse reports back. */
export interface AnimalSlaughterhouseData {
  baconCutlet: number | null;
  belly: number | null;
  fat: number | null;
  ham: number | null;
  hookId: string | null;
  leanMeat: number | null;
  money: number | null;
  /** Problems flagged by the slaughterhouse. Empty when there are none, never absent. */
  problems: string[];
  shoulder: number | null;
  spine: number | null;
  /** ISO date-time the slaughterhouse data was received. */
  timestamp: string | null;
  tradingClass: string | null;
  weight: number | null;
}

export const toAnimalSlaughterhouseData = (
  dto: HealthSystemDALModelAnimalsDataAnimalSlaughterhouseData
): AnimalSlaughterhouseData => ({
  baconCutlet: dto.baconCutlet ?? null,
  belly: dto.belly ?? null,
  fat: dto.fat ?? null,
  ham: dto.ham ?? null,
  hookId: dto.hookId ?? null,
  leanMeat: dto.leanMeat ?? null,
  money: dto.money ?? null,
  problems: dto.problems ?? [],
  shoulder: dto.shoulder ?? null,
  spine: dto.spine ?? null,
  timestamp: dto.timestamp ?? null,
  tradingClass: dto.tradingClass ?? null,
  weight: dto.weight ?? null
});

export const toAnimalWeight = (dto: HealthSystemDALModelAnimalsDataAnimalWeight): AnimalWeight => ({
  birthed: dto.birthed,
  weaned: dto.weaned,
  tenWeeks: dto.tenWeeks,
  slaughtered: dto.slaughtered,
  weightPerDay: dto.weightPerDay ?? null,
  weights: dto.weights ?? []
});

export interface AnimalNipples {
  left: number;
  right: number;
  /** Additional nipples counted after the belly-button check. */
  additionalLeft: number;
  additionalRight: number;
}

export interface AnimalNote {
  id: string;
  text: string | null;
  /** ISO date-time the note was created. */
  created: string;
}

export interface AnimalFosterMotherHistoryItem {
  id: string;
  fosterMotherId: string;
  fosterMotherNumber: string | null;
  /** ISO date-time of the transfer. */
  transferredAt: string;
  weightAtTransfer: AnimalWeightMeasurement;
}

/**
 * Only the fields the animal-view location line reads. See `src/models/location-data-model` notes
 * elsewhere: `room` is a free-text string, not a Room entity.
 */
export interface AnimalLocation {
  room: string | null;
  pen: string | null;
  number: string | null;
}

export interface AnimalDisease {
  name: string | null;
}

export interface AnimalMortalityReason {
  name: string | null;
}

export interface AnimalView {
  animalType: AnimalType;
  /** ISO date-time. Always present - the API never returns an animal without a birth date. */
  dateOfBirth: string;
  /** ISO date-time. Null while the animal is alive. */
  deathDate: string | null;
  /** Null when the animal has no mortality disease recorded, or was never marked as died. */
  disease: AnimalDisease | null;
  /** @format uuid */
  fatherId: string | null;
  /** Empty when the animal was never fostered. */
  fosterMotherHistory: AnimalFosterMotherHistoryItem[];
  /** @format uuid */
  fosterMotherId: string | null;
  gender: AnimalGender;
  genetics: string | null;
  isDied: boolean;
  isSoftDeleted: boolean;
  /** Null when the animal has no current location (e.g. slaughtered/removed). */
  location: AnimalLocation | null;
  /** Null unless `isDied` and a mortality reason was recorded. */
  mortalityReason: AnimalMortalityReason | null;
  /** @format uuid */
  motherId: string | null;
  /** Null for animal types that are never nippled-checked. */
  nipples: AnimalNipples | null;
  notes: AnimalNote[];
  number: string | null;
  /** Null until the slaughterhouse reports back for this animal. */
  slaughterhouseData: AnimalSlaughterhouseData | null;
  /** Null for animal types that carry no weight history. */
  weight: AnimalWeight | null;
  /** Empty until enough weight readings exist to chart a trend. */
  weightAnalytics: AnimalWeightAnalytic[];
  weightCategory: AnimalWeightCategory;
}

export const toAnimalView = (dto: HealthSystemDomainAnimalsResultAnimalViewModel): AnimalView => ({
  animalType: dto.animalType as unknown as AnimalType,
  dateOfBirth: dto.dateOfBirth,
  deathDate: dto.deathDate ?? null,
  disease: dto.disease ? { name: dto.disease.name ?? null } : null,
  fatherId: dto.fatherId ?? null,
  fosterMotherHistory: (dto.fosterMotherHistory ?? []).map((item) => ({
    id: item.id,
    fosterMotherId: item.fosterMotherId,
    fosterMotherNumber: item.fosterMotherNumber ?? null,
    transferredAt: item.transferredAt,
    weightAtTransfer: item.weightAtTransfer
  })),
  fosterMotherId: dto.fosterMotherId ?? null,
  gender: dto.gender as unknown as AnimalGender,
  genetics: dto.genetics ?? null,
  isDied: dto.isDied,
  isSoftDeleted: dto.isSoftDeleted,
  location: dto.location
    ? {
        room: dto.location.room ?? null,
        pen: dto.location.pen ?? null,
        number: dto.location.number ?? null
      }
    : null,
  mortalityReason: dto.mortalityReason ? { name: dto.mortalityReason.name ?? null } : null,
  motherId: dto.motherId ?? null,
  nipples: dto.nipples
    ? {
        left: dto.nipples.left,
        right: dto.nipples.right,
        additionalLeft: dto.nipples.additionalLeft,
        additionalRight: dto.nipples.additionalRight
      }
    : null,
  notes: (dto.notes ?? []).map((note) => ({ id: note.id, text: note.text ?? null, created: note.created })),
  number: dto.number ?? null,
  slaughterhouseData: dto.slaughterhouseData ? toAnimalSlaughterhouseData(dto.slaughterhouseData) : null,
  weight: dto.weight ? toAnimalWeight(dto.weight) : null,
  weightAnalytics: (dto.weightAnalytics ?? []).map((model) => ({
    name: model.name,
    chartData: model.chartData.map((point) => ({
      daysAge: point.daysAge,
      totalCount: point.totalCount,
      value: point.value
    }))
  })),
  weightCategory: dto.weightCategory as unknown as AnimalWeightCategory
});

/**
 * One non-fatal condition registered against a live animal - genetic failure, biter, bite victim
 * (AB#3724). `category`/`geneticFailure` are the backend's string enumeration ids, not numeric enums -
 * see `src/utils/animalObservations.ts`.
 */
export interface AnimalObservation {
  id: string;
  /** ISO date-time the observation was registered. */
  registeredAt: string;
  registeredBy: string | null;
  /** Free-text location the observation was registered at. */
  location: string | null;
  comment: string | null;
  category: string;
  geneticFailure: string | null;
  geneticFailureOther: string | null;
}

export const toAnimalObservation = (
  dto: HealthSystemDomainAnimalsModelsAnimalObservationViewModel
): AnimalObservation => ({
  id: dto.id,
  registeredAt: dto.registeredAt,
  registeredBy: dto.registeredBy ?? null,
  location: dto.location ?? null,
  comment: dto.comment ?? null,
  category: dto.category,
  geneticFailure: dto.geneticFailure ?? null,
  geneticFailureOther: dto.geneticFailureOther ?? null
});
